// src/services/epoVendorApi.js
//
// Round 22 — Vendor swap client. Mirrors POST /api/Epo/{n}/vendor.
// Round 23 — Attachment listing + download helpers also live here for
// proximity (a vendor/attachment surface page is a natural next step).
//
// All mutations require Finance/Babu/IT-Admin (R22 + R19 + R17 gating).
// Attachment reads stay open to any authenticated user.
import api from "../api";

// R22 — replace vendor wholesale on an EPO header. Only non-null fields apply.
export async function swapVendor(epoNum, { vendorId, company, street1, city, state, zip }) {
    const res = await api.post(`Epo/${epoNum}/vendor`, {
        VendorId: vendorId ?? null,
        Company:  company  ?? null,
        Street1:  street1  ?? null,
        City:     city     ?? null,
        State:    state    ?? null,
        Zip:      zip      ?? null,
    });
    return res.data;
}

// R23 — list attachments (metadata only; bytes are not in the payload).
export async function listAttachments(epoNum) {
    const res = await api.get(`Epo/${epoNum}/attachments`);
    return res.data; // [{ Id, FileName, Author, Timestamp, SizeBytes }, ...]
}

// R23 — produce a URL the FE can stick into an <a> for direct browser download.
// Axios baseURL is `/api/` so we return a relative URL with that prefix already
// resolved. If the project's REACT_APP_API_URL points elsewhere, the existing
// axios instance is what should be used — but for native browser downloads we
// need a full URL the browser can hit. Return the path; callers can prefix
// window.__API_URL__ or rely on dev-server proxy.
export function attachmentDownloadUrl(epoNum, attachmentId) {
    // Use the same axios base resolution. The api instance exposes its
    // baseURL via api.defaults.baseURL — we read it dynamically.
    const base = (api.defaults?.baseURL || "/api/").replace(/\/+$/, "");
    return `${base}/Epo/${epoNum}/attachment/${attachmentId}`;
}

// R47 — actually download an attachment.
//
// The earlier `attachmentDownloadUrl` returned a URL meant for a plain
// <a href> link — but the EPO API is `[Authorize]` and the JWT is
// attached by the axios interceptor as a Bearer header. Plain browser
// navigation does NOT send that header, so the server returns 401 and
// Chrome surfaces "File wasn't available on site".
//
// Fix: fetch the bytes via axios (auth header included), then trigger
// the download client-side via a Blob URL + programmatic anchor click.
export async function downloadAttachment(epoNum, attachmentId, fileName) {
    const res = await api.get(`Epo/${epoNum}/attachment/${attachmentId}`, {
        responseType: "blob",
    });
    const mime = res.headers?.["content-type"] || "application/octet-stream";
    const blob = new Blob([res.data], { type: mime });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url;
    a.download = fileName || `epo-${epoNum}-attachment-${attachmentId}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Free the blob shortly after the click so the download has time to start.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}
